
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { getDownloadURL, listAll, ref } from "firebase/storage";
import { storage } from "../../config/firebase";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const LoadingSpinner = () => (
  <div className="flex justify-center items-center p-6">
    <div className="w-7 h-7 border-4 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
    <p className="ml-3 text-blue-600 font-semibold text-lg">Loading...</p>
  </div>
);

const PaperViewer = () => {
  const [fileUrl, setFileUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [allEditions, setAllEditions] = useState([]);
  const [currentEdition, setCurrentEdition] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Listen for resize
  useEffect(() => {
    const resizeHandler = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", resizeHandler);
    return () => window.removeEventListener("resize", resizeHandler);
  }, []);

  // ----------------------------------------------------------------
  // Fetch PDF by edition
  // ----------------------------------------------------------------
  const fetchPdfByEdition = useCallback(async (editionName) => {
    try {
      setIsLoading(true);
      setError("");
      setFileUrl(null);
      setNumPages(null);
      setPageNumber(1);

      const postsRef = ref(storage, "posts");
      const listResult = await listAll(postsRef);

      const selectedRef = listResult.items.find(
        (item) => item.name === editionName
      );

      if (!selectedRef) {
        setError("Edition not found.");
        setIsLoading(false);
        return;
      }

      const url = await getDownloadURL(selectedRef);
      setFileUrl(url);
      setCurrentEdition(editionName);
    } catch (err) {
      console.error(err);
      setError("Failed to load PDF file.");
      setIsLoading(false);
    }
  }, []);

  // ----------------------------------------------------------------
  // Fetch All Editions
  // ----------------------------------------------------------------
  const fetchAllEditions = useCallback(async () => {
    try {
      const postsRef = ref(storage, "posts");
      const listResult = await listAll(postsRef);

      const editions = listResult.items
        .filter((item) => item.name.startsWith("Edition-"))
        .map((item) => {
          const dateText = item.name.replace("Edition-", "").replace(".pdf", "");
          return { name: item.name, date: dateText };
        });

      editions.sort((a, b) => b.date.localeCompare(a.date));
      setAllEditions(editions);

      if (editions.length > 0) {
        fetchPdfByEdition(editions[0].name);
      } else {
        setError("No editions found.");
        setIsLoading(false);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load editions.");
      setIsLoading(false);
    }
  }, [fetchPdfByEdition]);

  useEffect(() => {
    fetchAllEditions();
  }, [fetchAllEditions]);

  // ----------------------------------------------------------------
  // PDF load success
  // ----------------------------------------------------------------
  const handleDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setIsLoading(false);
  };

  // ----------------------------------------------------------------
  // Date selection
  // ----------------------------------------------------------------
  const handleDateSelect = (date) => {
    const ed = allEditions.find((e) => e.date === date);
    if (ed) {
      fetchPdfByEdition(ed.name);
      setIsCalendarOpen(false);
    }
  };

  // ----------------------------------------------------------------
  // Touch gesture handling
  // ----------------------------------------------------------------
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const delta = touchStartX.current - touchEndX.current;

    if (Math.abs(delta) < 50) return; // only strong swipes

    if (delta > 50) {
      // swipe left → next page
      if (pageNumber < numPages) setPageNumber((p) => p + 1);
    } else if (delta < -50) {
      // swipe right → previous page
      if (pageNumber > 1) setPageNumber((p) => p - 1);
    }
  };

  const pageWidth = windowWidth * 0.97;

  return (
    <div className="flex flex-col items-center w-full max-w-full px-3 py-4">

      {/* Title */}
      <h2 className="text-2xl font-extrabold text-gray-900 mb-4">
        Edition:{" "}
        <span className="text-blue-700">
          {currentEdition?.replace("Edition-", "").replace(".pdf", "")}
        </span>
      </h2>

      {/* Controls */}
      <div className="flex items-center gap-4 w-full max-w-3xl mb-4">
        
        {/* Page Buttons (horizontal scroll) */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-2 flex-grow shadow-inner overflow-x-auto whitespace-nowrap no-scrollbar">
          {Array.from({ length: Math.min(numPages || 0, 10) }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setPageNumber(page)}
              className={`px-3 py-1 rounded-lg font-semibold text-sm transition 
                ${
                  page === pageNumber
                    ? "bg-blue-600 text-white"
                    : "bg-white hover:bg-blue-50"
                }`}
            >
              {page}
            </button>
          ))}
          {numPages > 10 && <span className="text-gray-500 inline-block">...</span>}
        </div>

        {/* Calendar */}
        <div className="relative">
          <button
                onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                className="w-16 h-16 text-3xl flex items-center justify-center"
            >
            <svg
  xmlns="http://www.w3.org/2000/svg"
  fill="currentColor"
  viewBox="0 0 24 24"
  className="w-8 h-8 text-black-700"
>
  <path d="M19 4h-1V2h-2v2H8V2H6v2H5C3.9 4 3 4.9 3 6v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
</svg>


           </button>


          

          {isCalendarOpen && (
            <div className="absolute right-0 mt-2 w-64 bg-white border rounded-xl shadow-xl p-3 z-20">
              <p className="font-bold text-blue-700 mb-2">Available Editions</p>
              <div className="max-h-52 overflow-y-auto space-y-1">
                {allEditions.map((ed) => (
                  <button
                    key={ed.date}
                    onClick={() => handleDateSelect(ed.date)}
                    className={`w-full text-left p-2 rounded-lg 
                      ${
                        currentEdition === ed.name
                          ? "bg-blue-200 font-bold"
                          : "hover:bg-gray-100"
                      }`}
                  >
                    {ed.date}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setIsCalendarOpen(false)}
                className="text-red-500 mt-2 w-full hover:text-red-700 text-sm"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4 shadow w-full max-w-2xl text-center">
          {error}
        </div>
      )}

      {/* Single Spinner */}
      {isLoading && <LoadingSpinner />}

      {/* PDF View */}
      {fileUrl && (
        <div
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="w-full flex justify-center"
        >
          <Document file={fileUrl} onLoadSuccess={handleDocumentLoadSuccess}>
            <Page
              pageNumber={pageNumber}
              width={pageWidth}
              className="shadow-lg border rounded-xl"
            />
          </Document>
        </div>
      )}

      {/* Page Label */}
      {numPages && (
        <div className="mt-4 bg-blue-50 px-4 py-2 rounded-lg shadow-inner">
          <p className="text-lg font-bold text-blue-800">
            Page {pageNumber} of {numPages}
          </p>
        </div>
      )}
    </div>
  );
};

export default PaperViewer;
